import { joinSQLFragments, SQL, type SQLFragment } from '@/client/query'

import type { QueryModelV2 } from '.'
import type { AcceptedAttributes } from './types'

type AssociationDefinition = {
  query: QueryModelV2<AcceptedAttributes>
  foreignKey: SQLFragment<number>
  sourceKey: SQLFragment<number>
  paranoid?: boolean
}

export type AssociationDefinitions = Record<string, AssociationDefinition>

type IncludeAssociation = {
  association: string
  where?: SQLFragment<boolean> | null
  paranoid?: boolean
  required?: boolean
  include?: IncludeAssociations
}

export type IncludeAssociations = IncludeAssociation[]

export function includeAssociationsToSql(
  sourceQuery: QueryModelV2<AcceptedAttributes>,
  associations: IncludeAssociations
): SQLFragment<never> {
  if (associations == null || associations.length === 0) {
    return SQL``
  }

  return joinSQLFragments(
    associations.map(
      ({
        association: associationName,
        where = null,
        paranoid: specifiedParanoid,
        required = true,
        include = [],
      }) => {
        // default to inner join
        const associationDefinition = sourceQuery.associations()[associationName]
        if (associationDefinition == null) {
          const { __sqlFragment: sourceQueryName } = sourceQuery
          throw new Error(`Association ${associationName} not found on ${sourceQueryName}`)
        }

        const {
          query: foreignQuery,
          foreignKey,
          sourceKey,
          paranoid: defaultParanoid,
        } = associationDefinition

        const paranoid: boolean = specifiedParanoid ?? defaultParanoid ?? true
        const joinSql = required === false ? SQL`LEFT JOIN` : SQL`INNER JOIN`
        // TODO: where usage can be improved to make more convenient, right now have to do something
        // like include: [{ association: 'User', where: SQL`(SELECT ${User.countFollowers()}) > 0` }],
        const whereSql = where != null ? SQL`AND ${where}` : SQL``
        const deletedAtSql =
          paranoid && foreignQuery.deleted_at != null
            ? SQL`AND ${foreignQuery.deleted_at} IS NULL`
            : SQL``
        const recursiveJoin = includeAssociationsToSql(foreignQuery, include)

        return SQL`
          ${joinSql} ${foreignQuery} ON ${foreignKey} = ${sourceKey}
          ${whereSql}
          ${recursiveJoin}
          ${deletedAtSql}
        `
      }
    ),
    '\n'
  )
}
